import React, { useEffect, useState, useCallback } from 'react';
import { Box } from '@mui/material';

import { apiRequest } from '@/src/services';
import { getZeroOption, type Option, Constants } from '@/src/utils';
import { getValidSelectValue } from '@/src/utils/formUtils';
import {
    type InitialOptions,
    type SchoolClassOptions,
    type ClassOption,
    type SubmissionHistoryFilterState,
    type ExamOption,
} from '../models/filterDTOs';
import {
    SimpleButton,
    SimpleSingleSelect,
    FilterIcon,
    ICON_COLORS,
    ErrorMessage,
    FilterBarContainer,
} from '@/src/ui';

const AllOption = getZeroOption('All');
const LoadingOption = getZeroOption('Loading...');
const SelectDistrictOption = getZeroOption('Select District');
const SelectSchoolOption = getZeroOption('Select School');
const SelectGradeOption = getZeroOption('Select Grade');
const SelectClassOption = getZeroOption('Select Class');
const NoClassOption = getZeroOption('No Classes Available');
const NoExamOption = getZeroOption('No Exams Available');

interface SubmissionHistoryFiltersProps {
    filterState: SubmissionHistoryFilterState;
    setFilterState: React.Dispatch<React.SetStateAction<SubmissionHistoryFilterState>>;
    refreshTableData: (filters: SubmissionHistoryFilterState) => void | Promise<void>;
}

const requiredFields = ['districtId', 'schoolId', 'gradeLevelMasterId'] as const;
function getMissingFields(fs: SubmissionHistoryFilterState): string[] {
    return requiredFields
        .filter((key) => !fs[key] || Number(fs[key]) === 0)
        .map((key) => Constants.fieldLabels[key]);
}

const SubmissionHistoryFilters: React.FC<SubmissionHistoryFiltersProps> = ({
    filterState,
    setFilterState,
    refreshTableData,
}) => {
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [fieldsChanged, setFieldsChanged] = useState(false);

    const [districtOptions, setDistrictOptions] = useState<Option[]>([LoadingOption]);
    const [schoolOptions, setSchoolOptions] = useState<Option[]>([SelectSchoolOption]);
    const [gradeLevelMasterOptions, setGradeLevelMasterOptions] = useState<Option[]>([
        LoadingOption,
    ]);
    const [classOptions, setClassOptions] = useState<Option[]>([SelectClassOption]);
    const [examTypeMasterOptions, setExamTypeMasterOptions] = useState<Option[]>([AllOption]);
    const [examOptions, setExamOptions] = useState<Option[]>([AllOption]);
    const [classData, setClassData] = useState<ClassOption[]>([]);
    const [allExams, setAllExams] = useState<ExamOption[]>([]);
    const [filtersLoading, setFiltersLoading] = useState(true);

    const getLoadingProps = (opts: Option[], val: number, loading: boolean) =>
        loading
            ? { options: [LoadingOption], value: 0, disabled: true }
            : { options: opts, value: val };

    const updateClassOptions = useCallback(
        (data: ClassOption[], gradeLevelMasterId: number, schoolId: number) => {
            if (
                !schoolId ||
                schoolId === Number(SelectSchoolOption.id) ||
                !gradeLevelMasterId ||
                gradeLevelMasterId === Number(SelectGradeOption.id)
            ) {
                setClassOptions([SelectClassOption]);
                setFilterState((prev) => ({ ...prev, classId: Number(SelectClassOption.id) }));
                return;
            }

            const filtered = data
                .filter(
                    (option) =>
                        option.schoolId === schoolId &&
                        (!option.gradeLevelMasterIds?.length ||
                            option.gradeLevelMasterIds.includes(gradeLevelMasterId))
                )
                .map((option) => ({ id: option.id, name: option.name }));

            if (!filtered.length) {
                setClassOptions([NoClassOption]);
                setFilterState((prev) => ({ ...prev, classId: Number(NoClassOption.id) }));
            } else {
                setClassOptions([AllOption, ...filtered]);
                setFilterState((prev) => {
                    const classExists = filtered.some(
                        (opt) => Number(opt.id) === Number(prev.classId)
                    );
                    if (classExists) {
                        return prev;
                    }
                    return {
                        ...prev,
                        classId: Number(AllOption.id),
                    };
                });
            }
        },
        [setFilterState]
    );

    // Fetch schools + classes for a district
    const fetchSchoolClassOptions = useCallback(
        async (districtId: number) => {
            if (!districtId || districtId === Number(SelectDistrictOption.id)) {
                setSchoolOptions([SelectSchoolOption]);
                setClassOptions([SelectClassOption]);
                setClassData([]);
                setFilterState((prev) => ({
                    ...prev,
                    schoolId: Number(SelectSchoolOption.id),
                    classId: Number(SelectClassOption.id),
                }));
                return;
            }

            try {
                const res = await apiRequest<SchoolClassOptions>(
                    'GET',
                    `utility/filterData?getSchool=true&getClass=true&districtId=${districtId}`
                );
                const data = res.data;
                const newSchoolOptions = data.schoolOptions.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                setSchoolOptions([SelectSchoolOption, ...newSchoolOptions]);
                setFilterState((prev) => {
                    const schoolExists = newSchoolOptions.some(
                        (opt) => Number(opt.id) === Number(prev.schoolId)
                    );
                    return {
                        ...prev,
                        schoolId: schoolExists ? prev.schoolId : Number(SelectSchoolOption.id),
                    };
                });
                const sortedClassOptions = data.classOptions.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                setClassData(sortedClassOptions);
                setClassOptions([
                    SelectClassOption,
                    ...sortedClassOptions.map((option) => ({ id: option.id, name: option.name })),
                ]);
                setFilterState((prev) => {
                    const classExists = sortedClassOptions.some(
                        (opt) => Number(opt.id) === Number(prev.classId)
                    );
                    return {
                        ...prev,
                        classId: classExists ? prev.classId : Number(SelectClassOption.id),
                    };
                });
                setErrorMsg(null);
            } catch {
                setErrorMsg('Failed to fetch school and class options');
                setSchoolOptions([SelectSchoolOption]);
                setClassOptions([SelectClassOption]);
                setClassData([]);
            }
        },
        [setFilterState]
    );

    const filterExams = (
        exams: ExamOption[],
        schoolId: number | string,
        classId: number | string,
        examTypeMasterId: number | string
    ) => {
        if (!exams.length || !schoolId || Number(schoolId) === Number(AllOption.id)) {
            setExamOptions([AllOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(AllOption.id) }));
            return;
        }

        const filtered = exams.filter((exam) => {
            if (!exam.schoolIds?.length) return false;
            if (schoolId && !exam.schoolIds.includes(Number(schoolId))) return false;
            if (
                classId &&
                Number(classId) !== Number(AllOption.id) &&
                exam.classIds?.length &&
                !exam.classIds.includes(Number(classId))
            )
                return false;
            if (
                examTypeMasterId &&
                Number(examTypeMasterId) !== Number(AllOption.id) &&
                exam.examTypeMasterId !== Number(examTypeMasterId)
            )
                return false;
            return true;
        });

        const options = filtered.length
            ? [AllOption, ...filtered.map((e) => ({ id: e.id, name: e.name }))]
            : [NoExamOption];
        setExamOptions(options);

        setFilterState((prev) => ({
            ...prev,
            examId:
                filtered.length && prev.examId && filtered.some((e) => e.id === prev.examId)
                    ? prev.examId
                    : filtered.length
                      ? Number(AllOption.id)
                      : Number(NoExamOption.id),
        }));
    };

    const fetchExamOptions = async (
        examTypeMasterId: number | string,
        gradeLevelMasterId: number | string,
        districtId: number | string,
        schoolId: number | string
    ) => {
        if (!districtId || !schoolId || !gradeLevelMasterId) {
            setAllExams([]);
            setExamOptions([AllOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(AllOption.id) }));
            return;
        }

        try {
            setExamOptions([LoadingOption]);
            const res = await apiRequest<{ examOptions: ExamOption[] }>(
                'GET',
                `utility/filterData?getExam=true&gradeLevelMasterIds=${gradeLevelMasterId}&showPublishedExamsOnly=true`
            );

            const exams = (res.data.examOptions ?? [])
                .filter((exam) => exam.schoolIds?.length)
                .sort((a, b) => a.name.localeCompare(b.name));

            setAllExams(exams);
            filterExams(exams, schoolId, filterState.classId, examTypeMasterId);
            setErrorMsg(null);
        } catch {
            setAllExams([]);
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(AllOption.id) }));
            setErrorMsg('Failed to load exams');
        }
    };

    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getGradeLevelMaster=true&getDistrict=true&getExamTypeMaster=true'
                );
                const data = res.data;

                setGradeLevelMasterOptions([SelectGradeOption, ...data.gradeLevelMasterOptions]);
                setDistrictOptions([
                    SelectDistrictOption,
                    ...data.districtOptions.sort((a, b) => a.name.localeCompare(b.name)),
                ]);
                setExamTypeMasterOptions([
                    AllOption,
                    ...data.examTypeMasterOptions.sort((a, b) => a.name.localeCompare(b.name)),
                ]);

                if (
                    filterState &&
                    filterState.districtId &&
                    filterState.districtId !== Number(SelectDistrictOption.id)
                ) {
                    await fetchSchoolClassOptions(filterState.districtId);
                    setFiltersLoading(false);
                } else {
                    setFiltersLoading(false);
                }
            } catch {
                setErrorMsg('Failed to load initial filter options.');
                setFiltersLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchSchoolClassOptions(filterState.districtId);
    }, [filterState.districtId, fetchSchoolClassOptions]);

    useEffect(() => {
        if (
            filterState &&
            filterState.schoolId &&
            filterState.schoolId !== Number(SelectSchoolOption.id) &&
            filterState.gradeLevelMasterId &&
            filterState.gradeLevelMasterId !== Number(SelectGradeOption.id) &&
            classData.length > 0
        ) {
            updateClassOptions(classData, filterState.gradeLevelMasterId, filterState.schoolId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classData, filterState.schoolId, filterState.gradeLevelMasterId]);

    useEffect(() => {
        if (!filtersLoading) {
            fetchExamOptions(
                filterState.examTypeMasterId,
                filterState.gradeLevelMasterId,
                filterState.districtId,
                filterState.schoolId
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        filtersLoading,
        classData,
        filterState.gradeLevelMasterId,
        filterState.schoolId,
        filterState.examTypeMasterId,
        filterState.districtId,
        updateClassOptions,
    ]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="district-select"
                    label="District *"
                    {...getLoadingProps(
                        districtOptions,
                        Number(
                            getValidSelectValue(
                                filterState.districtId,
                                districtOptions,
                                districtOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newDistrictId = Number(e.target.value);
                        const newState = {
                            ...filterState,
                            districtId: newDistrictId,
                            schoolId: Number(SelectSchoolOption.id),
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(SelectClassOption.id),
                        };
                        setFilterState(newState);
                        fetchSchoolClassOptions(newDistrictId);
                        setExamOptions([AllOption]);
                        setAllExams([]);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="school-select"
                    label="School *"
                    {...getLoadingProps(
                        schoolOptions,
                        Number(
                            getValidSelectValue(
                                filterState.schoolId,
                                schoolOptions,
                                schoolOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newSchoolId = Number(e.target.value);
                        const newState = {
                            ...filterState,
                            schoolId: newSchoolId,
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(SelectClassOption.id),
                        };
                        setFilterState(newState);
                        updateClassOptions(classData, Number(SelectGradeOption.id), newSchoolId);
                        setExamOptions([AllOption]);
                        setAllExams([]);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading || filterState.districtId === Number(SelectDistrictOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="grade-select"
                    label="Grade Level *"
                    {...getLoadingProps(
                        gradeLevelMasterOptions,
                        Number(
                            getValidSelectValue(
                                filterState.gradeLevelMasterId,
                                gradeLevelMasterOptions,
                                gradeLevelMasterOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newGradeLevelMasterId = Number(e.target.value);
                        const newState = {
                            ...filterState,
                            gradeLevelMasterId: newGradeLevelMasterId,
                        };
                        setFilterState(newState);
                        updateClassOptions(classData, newGradeLevelMasterId, newState.schoolId);
                        fetchExamOptions(
                            newState.examTypeMasterId,
                            newState.gradeLevelMasterId,
                            newState.districtId,
                            newState.schoolId
                        );
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading || filterState.schoolId === Number(SelectSchoolOption.id)
                    }
                />
                <SimpleSingleSelect
                    name="class-select"
                    label="Class"
                    {...getLoadingProps(
                        classOptions,
                        Number(
                            getValidSelectValue(
                                filterState.classId,
                                classOptions,
                                classOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({ ...prev, classId: Number(e.target.value) }));
                        if (allExams.length) {
                            filterExams(
                                allExams,
                                filterState.schoolId,
                                Number(e.target.value),
                                filterState.examTypeMasterId
                            );
                        }
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        filterState.schoolId === Number(SelectSchoolOption.id) ||
                        filterState.gradeLevelMasterId === Number(SelectGradeOption.id)
                    }
                />
            </FilterBarContainer>
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="exam-type-select"
                    label="Type"
                    value={filterState.examTypeMasterId}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newExamTypeMasterId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            examTypeMasterId: newExamTypeMasterId,
                        }));
                        if (allExams.length) {
                            filterExams(
                                allExams,
                                filterState.schoolId,
                                filterState.classId,
                                newExamTypeMasterId
                            );
                        }
                    }}
                    options={examTypeMasterOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="exam-select"
                    label="Assessment"
                    value={filterState.examId}
                    onChange={(e) =>
                        setFilterState((prev) => ({ ...prev, examId: Number(e.target.value) }))
                    }
                    options={examOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleButton
                    onClick={async () => {
                        const missing = getMissingFields(filterState);
                        if (missing.length) {
                            setErrorMsg(`Please select: ${missing.join(', ')} before filtering.`);
                            return;
                        }
                        setErrorMsg(null);
                        setFieldsChanged(false);
                        await refreshTableData(filterState);
                    }}
                    variant={fieldsChanged ? 'outlined' : 'contained'}
                    color="primary"
                    sx={{ minHeight: 40, height: 40, alignSelf: 'center', padding: '8px 25px' }}
                >
                    <FilterIcon
                        color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                        style={{ width: 16, height: 16, marginRight: 8 }}
                    />
                    Filter
                </SimpleButton>
            </FilterBarContainer>

            {errorMsg && <ErrorMessage error={errorMsg} />}
        </Box>
    );
};

export default SubmissionHistoryFilters;
